import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { errorHandler, successHandler } from "../../../helpers/handlers";
import api from "../../../config/axios";
import { logout } from "../auth";
import { deleteDocument, uploadDocument } from "../../../config/firebase";

const initialState = {
  article: {
    posts: {
      data: [],
      lastDocId: null,
      hasMoreContent: false,
    },
    drafts: {
      data: [],
      lastDocId: null,
      hasMoreContent: false,
    },
  },
  school: {
    posts: {
      data: [],
      lastDocId: null,
      hasMoreContent: false,
    },
    drafts: {
      data: [],
      lastDocId: null,
      hasMoreContent: false,
    },
  },
  scholarship: {
    posts: {
      data: [],
      lastDocId: null,
      hasMoreContent: false,
    },
    drafts: {
      data: [],
      lastDocId: null,
      hasMoreContent: false,
    },
  },
  country: {
    posts: {
      data: [],
      lastDocId: null,
      hasMoreContent: false,
    },
    drafts: {
      data: [],
      lastDocId: null,
      hasMoreContent: false,
    },
  },
  selectedPost: null,
  pageLoading: false,
  actionLoading: false,
  error: null,
  currentTab: "posts",
};

const slice = createSlice({
  name: "posts",
  initialState,
  reducers: {
    setSelectedPost: (state, action) => {
      state.selectedPost = action.payload;
    },
    storeCurrentTab: (state, action) => {
      state.currentTab = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout.fulfilled, () => initialState)

      // Fetching posts
      .addCase(fetchPosts.pending, (state) => {
        state.pageLoading = true;
      })
      .addCase(fetchPosts.fulfilled, (state, action) => {
        const { category, isDraft, data, hasMoreContent } = action.payload;
        if (isDraft === true) {
          if (action.payload.reset) {
            state[category].drafts.data = [];
          }
          state[category].drafts.data = [
            ...state[category].drafts.data.filter(
              (existingItem) =>
                !data.data.some((newItem) => newItem.id === existingItem.id)
            ),
            ...data.data,
          ];
          state[category].drafts.lastDocId = data.lastDocId;
          state[category].drafts.hasMoreContent = hasMoreContent;
        } else {
          if (action.payload.reset) {
            state[category].posts.data = [];
          }
          state[category].posts.data = [
            ...state[category].posts.data.filter(
              (existingItem) =>
                !data.data.some((newItem) => newItem.id === existingItem.id)
            ),
            ...data.data,
          ];
          state[category].posts.lastDocId = data.lastDocId;
          state[category].posts.hasMoreContent = hasMoreContent;
        }

        state.pageLoading = false;
        state.error = null;
      })
      .addCase(fetchPosts.rejected, (state, action) => {
        state.error = action.payload;
        state.pageLoading = false;
      })

      // Creating a post
      .addCase(createPost.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(createPost.fulfilled, (state, action) => {
        state.actionLoading = false;
      })
      .addCase(createPost.rejected, (state, action) => {
        state.error = action.payload;
        state.actionLoading = false;
      })

      .addCase(publishPost.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(publishPost.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(publishPost.rejected, (state, action) => {
        state.error = action.payload;
        state.actionLoading = false;
      })

      // Updating a post
      .addCase(updatePost.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updatePost.fulfilled, (state, action) => {
        state.selectedPost = action.payload;
        state.actionLoading = false;
      })
      .addCase(updatePost.rejected, (state, action) => {
        const category = action.payload.category;
        state[category].error = action.payload;
        state.actionLoading = false;
      })

      // Deleting a post
      .addCase(deletePost.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deletePost.fulfilled, (state, action) => {
        const { category, isDraft } = action.payload;
        if (isDraft === true) {
          state[category].drafts.data = state[category].drafts.data.filter(
            (post) => post.id !== action.payload.id
          );
        } else {
          state[category].posts.data = state[category].posts.data.filter(
            (post) => post.id !== action.payload.id
          );
        }
        state.actionLoading = false;
      })
      .addCase(deletePost.rejected, (state) => {
        state.actionLoading = false;
      })

      .addCase(fetchPostsById.pending, (state) => {
        state.pageLoading = true;
      })
      .addCase(fetchPostsById.fulfilled, (state, action) => {
        state.selectedPost = action.payload;
        state.pageLoading = false;
      })
      .addCase(fetchPostsById.rejected, (state) => {
        state.pageLoading = false;
      });
  },
});

// Fetch posts based on category
export const fetchPosts = createAsyncThunk(
  "posts/fetch",
  async (
    { isDraft, category, lastDocId = "", limit = 10 },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.get(
        `/admin/posts?category=${category}&limit=${limit}&lastDocId=${lastDocId}&isDraft=${isDraft}`
      );
      return {
        category,
        isDraft,
        data: response.data.payload,
        reset: lastDocId === "",
        hasMoreContent: response.data.payload.data.length === limit,
      };
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPostsById = createAsyncThunk(
  "posts/fetch/:id",
  async ({ isDraft, id }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      // Only send isDraft when the caller knows; omit so the API can
      // resolve draft vs published by trying both collections.
      if (isDraft === true || isDraft === false || isDraft === "true" || isDraft === "false") {
        params.set("isDraft", String(isDraft));
      }
      const qs = params.toString();
      const response = await api.get(
        `/admin/posts/${id}${qs ? `?${qs}` : ""}`
      );
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

// Create a post
export const createPost = createAsyncThunk(
  "posts/create",
  async (data, { rejectWithValue }) => {
    try {
      if (typeof data.thumbnail !== "string") {
        data.thumbnail = await uploadDocument(data.thumbnail);
      }
      const response = await api.post(`/admin/posts`, data);
      successHandler(response.data.message);
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

// Update a post
export const updatePost = createAsyncThunk(
  "posts/update",
  async ({ id, data, oldThumbnail }, { rejectWithValue }) => {
    try {
      if (typeof data.thumbnail !== "string") {
        data.thumbnail = await uploadDocument(data.thumbnail);
      } else {
        // only delete oldThumbnail if there is a new one
        oldThumbnail = null;
      }
      const response = await api.put(`/admin/posts/${id}`, data);
      successHandler(response.data.message);
      deleteDocument(oldThumbnail);
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const publishPost = createAsyncThunk(
  "posts/publish",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/admin/posts/${id}/publish`);
      successHandler(response.data.message);
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

// Delete a post
export const deletePost = createAsyncThunk(
  "posts/delete",
  async ({ isDraft, id, category }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `/admin/posts/${id}?isDraft=${isDraft}`
      );
      successHandler(response.data.message);
      return { id, category, isDraft };
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export default slice.reducer;

export const { setSelectedPost, storeCurrentTab } = slice.actions;
